import { AlertOctagon, CircleStop, RefreshCw, ShieldAlert, ShieldCheck, ShoppingCart, WalletCards } from "lucide-react";
import { FormEvent, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, postJson } from "./api";
import { useSessionStore } from "./store";

type Row = Record<string, any>;

function ActionMessage({ value }: { value: { tone: "ok" | "error"; text: string } | null }) {
  if (!value) return null;
  return <div className={`action-message ${value.tone}`}>{value.text}</div>;
}

export default function ControlCenter() {
  const authenticated = useSessionStore((state) => state.authenticated);
  const client = useQueryClient();
  const systemQuery = useQuery({ queryKey: ["control-system"], queryFn: () => api<Row>("/api/system/status"), refetchInterval: 10_000 });
  const positionsQuery = useQuery({
    queryKey: ["control-positions"],
    queryFn: () => api<Row[]>("/api/positions"),
    enabled: authenticated,
    refetchInterval: 10_000,
  });
  const [armAuto, setArmAuto] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [ackLive, setAckLive] = useState(false);
  const [message, setMessage] = useState<{ tone: "ok" | "error"; text: string } | null>(null);
  const [busy, setBusy] = useState("");
  const [sellMarket, setSellMarket] = useState("");
  const [sellPct, setSellPct] = useState(100);
  const system = systemQuery.data || {};
  const auto = Boolean(system.auto_trading_active ?? system.auto_trading);
  const autoRequested = Boolean(system.auto_trading_requested ?? auto);
  const tradingReady = Boolean(system.trading_ready ?? true);
  const pauseReason = String(system.auto_pause_reason || "");
  const killed = Boolean(system.kill_switch);
  const autoState = auto ? "ACTIVE" : autoRequested ? (tradingReady ? "ARMED / PAUSED" : "RECOVERING") : "OFF";
  const sellPosition = (positionsQuery.data || []).find((row) => String(row.market || "") === sellMarket);
  const sellHeldQuantity = Math.max(0, Number(sellPosition?.total_quantity || 0));
  const sellVolume = sellHeldQuantity * Math.max(0, Math.min(100, sellPct)) / 100;
  const autoDescription = auto
    ? "새로운 실주문이 발생할 수 있습니다."
    : autoRequested
      ? pauseReason === "kill_switch"
        ? "AUTO 요청은 유지 중이며 Kill Switch 해제 후 자동 재개될 수 있습니다."
        : pauseReason === "daily_equity_unverified"
          ? "AUTO 요청은 유지 중이며 일일 자산 원장 검증 전이라 신규 진입을 차단합니다."
          : "AUTO 요청은 유지 중이며 복구 절차가 끝날 때까지 신규 진입을 차단합니다."
      : "분석과 모니터링만 수행 중입니다.";

  async function act(key: string, task: () => Promise<unknown>, success: string) {
    setBusy(key);
    setMessage(null);
    try {
      await task();
      setMessage({ tone: "ok", text: success });
      await Promise.all([
        client.invalidateQueries({ queryKey: ["control-system"] }),
        client.invalidateQueries({ queryKey: ["dashboard"] }),
        client.invalidateQueries({ queryKey: ["control-positions"] }),
      ]);
    } catch (error) {
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "작업에 실패했습니다." });
    } finally {
      setBusy("");
    }
  }

  if (!authenticated) {
    return <div className="page-stack"><div className="page-intro"><div><span className="eyebrow">CONTROL CENTER</span><h2>운영 조작은 관리자만 가능합니다</h2><p>조회 화면과 분리된 고위험 작업 영역입니다. 상단에서 로그인해 주세요.</p></div></div><div className="control-lock"><ShieldCheck size={30} /><strong>관리자 세션 필요</strong><span>모든 실행 요청은 Worker를 거쳐 FastAPI 인증과 RiskEngine을 다시 통과합니다.</span></div></div>;
  }

  async function enableAuto() {
    if (confirmText.trim().toUpperCase() !== "AUTO ON" || !ackLive) return;
    await act("auto-on", () => postJson("/api/system/auto-trading", { enabled: true }), "자동매매 시작 요청을 적용했습니다.");
    setArmAuto(false);
    setConfirmText("");
    setAckLive(false);
  }

  async function disableAuto() {
    await act("auto-off", () => postJson("/api/system/auto-trading", { enabled: false }), "자동매매를 중지했습니다.");
  }

  async function activateKill() {
    const reason = window.prompt("긴급 정지 사유를 입력하세요.", "관리자 수동 긴급 정지")?.trim();
    if (!reason) return;
    await act("kill-on", () => postJson("/api/system/kill-switch/activate", { reason }), "Kill Switch를 활성화했습니다. 신규 진입이 차단됩니다.");
  }

  async function deactivateKill() {
    const resumeNotice = autoRequested
      ? "\nAUTO 요청이 유지 중이므로 다른 안전 게이트가 정상이라면 자동매매가 재개될 수 있습니다."
      : "";
    if (!window.confirm(`Kill Switch를 해제하시겠습니까? 신규 진입이 다시 가능해질 수 있습니다.${resumeNotice}`)) return;
    await act("kill-off", () => postJson("/api/system/kill-switch/deactivate", {}), "Kill Switch를 해제했습니다.");
  }

  async function syncLive() {
    await act("sync", () => postJson("/api/system/sync-live-state", {}), "실제 업비트 잔고와 주문 상태를 동기화했습니다.");
  }

  async function manualBuy(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const market = String(form.get("market") || "").trim().toUpperCase();
    const amount = Number(form.get("amount") || 0);
    if (!market || amount < 5_500) return setMessage({ tone: "error", text: "종목과 5,500원 이상의 주문금액을 확인해 주세요." });
    const available = Math.round(Number(system.available_krw || 0)).toLocaleString("ko-KR");
    if (!window.confirm(`${market}를 ${Math.round(amount).toLocaleString("ko-KR")}원 시장가 매수하시겠습니까?\n현재 가용 KRW: ${available}원\nRiskEngine이 최종 허용 여부를 다시 검사합니다.\n실제 주문입니다.`)) return;
    await act("buy", () => postJson("/api/manual/buy", { market, amount_krw: amount }), `${market} 수동 매수 요청을 전송했습니다.`);
    event.currentTarget.reset();
  }

  async function manualSell(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const market = sellMarket.trim().toUpperCase();
    const volume = sellVolume;
    if (!market || volume <= 0) return setMessage({ tone: "error", text: "보유 포지션과 매도 비율을 확인해 주세요." });
    if (!window.confirm(`${market} 보유량의 ${sellPct}% (${volume}개)를 시장가 매도하시겠습니까?\n서버가 실제 보유수량을 다시 동기화하고 reduce-only 검증을 수행합니다.\n실제 주문입니다.`)) return;
    await act("sell", () => postJson("/api/manual/sell", { market, volume }), `${market} 수동 매도 요청을 전송했습니다.`);
    setSellPct(100);
  }

  return <div className="page-stack">
    <div className="page-intro"><div><span className="eyebrow">CONTROL CENTER</span><h2>실제 자산에 영향을 주는 작업</h2><p>조회·분석과 분리된 실행 영역입니다. 자동매매 시작만 2단계 확인을 요구하고 중지는 즉시 수행합니다.</p></div></div>
    <ActionMessage value={message} />

    <section className="control-grid">
      <article className={`control-card ${auto ? "danger-zone" : "safe-zone"}`}>
        <div className="control-card__icon"><CircleStop size={24} /></div>
        <div className="control-card__body"><span>자동매매</span><strong>{autoState}</strong><p>{autoDescription}</p></div>
        <div className="control-card__actions">{autoRequested ? <button className="button danger" disabled={busy === "auto-off"} onClick={disableAuto}>AUTO 요청 중지</button> : <button className="button primary" onClick={() => setArmAuto(true)}>시작 절차</button>}</div>
      </article>

      <article className={`control-card ${killed ? "danger-zone" : "safe-zone"}`}>
        <div className="control-card__icon"><ShieldAlert size={24} /></div>
        <div className="control-card__body"><span>Kill Switch</span><strong>{killed ? "활성" : "해제"}</strong><p>{killed ? String(system.kill_reason || "신규 진입 차단") : "RiskEngine이 정상적으로 신규 진입을 평가합니다."}</p></div>
        <div className="control-card__actions">{killed ? <button className="button warning" disabled={busy === "kill-off"} onClick={deactivateKill}>정지 해제</button> : <button className="button danger" disabled={busy === "kill-on"} onClick={activateKill}>긴급 정지</button>}</div>
      </article>

      <article className="control-card">
        <div className="control-card__icon"><RefreshCw size={24} /></div>
        <div className="control-card__body"><span>라이브 동기화</span><strong>업비트 상태 확인</strong><p>잔고·주문 상태를 거래소와 다시 맞춥니다.</p></div>
        <div className="control-card__actions"><button className="button soft" disabled={busy === "sync"} onClick={syncLive}>잔고 동기화</button></div>
      </article>
    </section>

    {armAuto && !autoRequested && <section className="arming-panel">
      <div className="arming-panel__title"><AlertOctagon size={24} /><div><span className="eyebrow">LIVE TRADING CONFIRMATION</span><h3>자동매매 시작 확인</h3></div></div>
      <div className="risk-summary"><div><span>거래 모드</span><strong>LIVE ONLY</strong></div><div><span>Kill Switch</span><strong>{killed ? "활성 — 시작 불가" : "해제"}</strong></div><div><span>RiskEngine</span><strong>모든 주문 강제 적용</strong></div></div>
      <label className="confirm-check"><input type="checkbox" checked={ackLive} onChange={(e) => setAckLive(e.target.checked)} /><span>실제 원화 자산으로 주문이 발생할 수 있음을 확인했습니다.</span></label>
      <label className="confirm-field"><span>확인을 위해 <b>AUTO ON</b> 입력</span><input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="AUTO ON" /></label>
      <div className="action-row"><button className="button soft" onClick={() => setArmAuto(false)}>취소</button><button className="button primary" disabled={killed || !ackLive || confirmText.trim().toUpperCase() !== "AUTO ON" || busy === "auto-on"} onClick={enableAuto}>자동매매 시작</button></div>
    </section>}

    <section className="manual-grid">
      <form className="manual-card" onSubmit={manualBuy}><div className="manual-card__title"><ShoppingCart size={20} /><div><span>수동 매수</span><strong>시장가 KRW 매수</strong></div></div><label><span>마켓</span><input name="market" placeholder="KRW-XRP" /></label><label><span>금액 (KRW)</span><input name="amount" type="number" min="5500" step="1000" placeholder="500000" /></label><button className="button primary" type="submit" disabled={busy === "buy"}>실제 매수 확인</button></form>
      <form className="manual-card" onSubmit={manualSell}><div className="manual-card__title"><WalletCards size={20} /><div><span>수동 매도</span><strong>보유 포지션 기준 reduce-only</strong></div></div><label><span>보유 포지션</span><select value={sellMarket} onChange={(e) => setSellMarket(e.target.value)} required><option value="">선택</option>{(positionsQuery.data || []).map((row) => <option key={row.market} value={row.market}>{row.market} · {Number(row.total_quantity || 0).toLocaleString("ko-KR", { maximumFractionDigits: 8 })}개</option>)}</select></label><label><span>매도 비율</span><select value={sellPct} onChange={(e) => setSellPct(Number(e.target.value))}><option value={25}>25%</option><option value={50}>50%</option><option value={100}>100%</option></select></label><p className="muted">예상 매도수량 {sellVolume > 0 ? sellVolume.toLocaleString("ko-KR", { maximumFractionDigits: 8 }) : "-"}개 · 실제 주문 직전 거래소 보유량을 다시 검증합니다.</p><button className="button danger" type="submit" disabled={busy === "sell" || !sellMarket || sellVolume <= 0}>실제 매도 확인</button></form>
    </section>
  </div>;
}
